/**
 * MicroscopeHUD - Heads-up display for microscope controls
 * Bio-luminescent microscopy theme with zoom slider and controls
 */

import Phaser from 'phaser';
import { MicroscopeSystem } from '@/game/systems';
import type { ZoomEvent } from '@/game/systems';

export interface MicroscopeHUDConfig {
  /** Position X (default: right side) */
  x?: number;
  /** Position Y (default: center) */
  y?: number;
  /** Width of the control panel */
  width?: number;
  /** Height of the control panel */
  height?: number;
}

interface ZoomButton {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  isActive: boolean;
}

/**
 * MicroscopeHUD provides visual controls for the microscope system
 */
export class MicroscopeHUD {
  private scene: Phaser.Scene;
  private microscope: MicroscopeSystem;
  private config: Required<MicroscopeHUDConfig>;

  /** Main container */
  private container!: Phaser.GameObjects.Container;

  /** UI elements */
  private zoomButtons: ZoomButton[] = [];
  private zoomLabel!: Phaser.GameObjects.Text;
  private scannerButton!: Phaser.GameObjects.Container;
  private scaleIndicator!: Phaser.GameObjects.Text;

  /** Colors */
  private static readonly COLORS = {
    panelBg: 0x0a1628,
    panelBorder: 0x1e3a5f,
    buttonBg: 0x1a365d,
    buttonBgHover: 0x2d4a6f,
    buttonBgActive: 0x2b6cb0,
    textPrimary: 0xe2e8f0,
    textSecondary: 0x90cdf4,
    accent: 0x4299e1,
    accentGlow: 0x68d391,
    scannerActive: 0x48bb78,
  };

  constructor(
    scene: Phaser.Scene,
    microscope: MicroscopeSystem,
    config: MicroscopeHUDConfig = {}
  ) {
    this.scene = scene;
    this.microscope = microscope;

    const camera = scene.cameras.main;
    this.config = {
      x: config.x ?? camera.width - 80,
      y: config.y ?? camera.height / 2,
      width: config.width ?? 60,
      height: config.height ?? 400,
    };

    this.create();
    this.setupEventListeners();
  }

  /**
   * Create all HUD elements
   */
  private create(): void {
    this.container = this.scene.add.container(this.config.x, this.config.y);
    this.container.setDepth(1000);

    this.createPanel();
    this.createZoomButtons();
    this.createZoomLabel();
    this.createScannerButton();
    this.createScaleIndicator();

    this.updateActiveZoom();
  }

  /**
   * Create the main panel background
   */
  private createPanel(): void {
    const { width, height } = this.config;
    const graphics = this.scene.add.graphics();

    // Panel shadow
    graphics.fillStyle(0x000000, 0.3);
    graphics.fillRoundedRect(-width / 2 + 3, -height / 2 + 3, width, height, 10);

    // Panel background
    graphics.fillStyle(MicroscopeHUD.COLORS.panelBg, 0.9);
    graphics.fillRoundedRect(-width / 2, -height / 2, width, height, 10);

    // Panel border with glow effect
    graphics.lineStyle(2, MicroscopeHUD.COLORS.panelBorder, 0.8);
    graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 10);

    // Inner glow line
    graphics.lineStyle(1, MicroscopeHUD.COLORS.accent, 0.3);
    graphics.strokeRoundedRect(-width / 2 + 2, -height / 2 + 2, width - 4, height - 4, 8);

    this.container.add(graphics);
  }

  /**
   * Create zoom level buttons
   */
  private createZoomButtons(): void {
    const zoomLevels = MicroscopeSystem.ZOOM_LEVELS;
    const buttonHeight = 35;
    const spacing = 5;
    const startY = -this.config.height / 2 + 50;

    zoomLevels.forEach((level, index) => {
      const y = startY + index * (buttonHeight + spacing);
      const button = this.createZoomButton(level, 0, y);
      this.zoomButtons.push(button);
    });
  }

  /**
   * Create a single zoom button
   */
  private createZoomButton(level: number, x: number, y: number): ZoomButton {
    const buttonWidth = 50;
    const buttonHeight = 30;

    const container = this.scene.add.container(x, y);

    // Button background
    const bg = this.scene.add.graphics();
    this.drawButtonBg(bg, buttonWidth, buttonHeight, false, false);

    // Label
    const labelText = level >= 1000 ? '1000x' : `${level}x`;
    const label = this.scene.add.text(0, 0, labelText, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: level >= 1000 ? '10px' : '12px',
      color: '#e2e8f0',
    });
    label.setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(buttonWidth, buttonHeight);
    container.setInteractive({ useHandCursor: true });

    // Hover effects
    container.on('pointerover', () => {
      if (!this.isLevelActive(level)) {
        this.drawButtonBg(bg, buttonWidth, buttonHeight, true, false);
      }
    });

    container.on('pointerout', () => {
      if (!this.isLevelActive(level)) {
        this.drawButtonBg(bg, buttonWidth, buttonHeight, false, false);
      }
    });

    container.on('pointerdown', () => {
      this.microscope.setZoomLevel(level);
    });

    this.container.add(container);

    return { container, bg, label, isActive: false };
  }

  /**
   * Draw button background
   */
  private drawButtonBg(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    hover: boolean,
    active: boolean
  ): void {
    graphics.clear();

    const bgColor = active
      ? MicroscopeHUD.COLORS.buttonBgActive
      : hover
        ? MicroscopeHUD.COLORS.buttonBgHover
        : MicroscopeHUD.COLORS.buttonBg;

    graphics.fillStyle(bgColor, 0.9);
    graphics.fillRoundedRect(-width / 2, -height / 2, width, height, 5);

    if (active) {
      // Glow effect for active button
      graphics.lineStyle(2, MicroscopeHUD.COLORS.accentGlow, 0.8);
      graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 5);
    } else {
      graphics.lineStyle(1, MicroscopeHUD.COLORS.accent, 0.5);
      graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 5);
    }
  }

  /**
   * Check if a zoom level is currently active
   */
  private isLevelActive(level: number): boolean {
    return this.microscope.getZoomLevel() === level;
  }

  /**
   * Create zoom level label
   */
  private createZoomLabel(): void {
    const y = -this.config.height / 2 + 20;

    this.zoomLabel = this.scene.add.text(0, y, 'ZOOM', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      color: '#90cdf4',
    });
    this.zoomLabel.setOrigin(0.5);

    this.container.add(this.zoomLabel);
  }

  /**
   * Create scanner toggle button
   */
  private createScannerButton(): void {
    const y = this.config.height / 2 - 50;
    const buttonSize = 40;

    this.scannerButton = this.scene.add.container(0, y);

    // Button background
    const bg = this.scene.add.graphics();
    bg.fillStyle(MicroscopeHUD.COLORS.buttonBg, 0.9);
    bg.fillCircle(0, 0, buttonSize / 2);
    bg.lineStyle(2, MicroscopeHUD.COLORS.accent, 0.7);
    bg.strokeCircle(0, 0, buttonSize / 2);

    // Scanner icon (simple crosshair)
    const icon = this.scene.add.graphics();
    this.drawScannerIcon(icon, false);

    // Label
    const label = this.scene.add.text(0, buttonSize / 2 + 10, 'SCAN', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '9px',
      color: '#90cdf4',
    });
    label.setOrigin(0.5);

    this.scannerButton.add([bg, icon, label]);
    this.scannerButton.setSize(buttonSize, buttonSize);
    this.scannerButton.setInteractive({ useHandCursor: true });

    // Click to toggle scanner
    this.scannerButton.on('pointerdown', () => {
      const isActive = this.microscope.getState().scannerActive;
      this.microscope.setScannerActive(!isActive);
      this.updateScannerButton(!isActive);
    });

    this.container.add(this.scannerButton);
  }

  /**
   * Draw scanner icon
   */
  private drawScannerIcon(graphics: Phaser.GameObjects.Graphics, active: boolean): void {
    graphics.clear();

    const color = active ? MicroscopeHUD.COLORS.scannerActive : MicroscopeHUD.COLORS.textSecondary;
    const size = 12;

    graphics.lineStyle(2, color, 1);

    // Crosshair
    graphics.lineBetween(-size, 0, size, 0);
    graphics.lineBetween(0, -size, 0, size);

    // Corner brackets
    graphics.lineBetween(-size, -size + 4, -size, -size);
    graphics.lineBetween(-size, -size, -size + 4, -size);

    graphics.lineBetween(size, -size + 4, size, -size);
    graphics.lineBetween(size, -size, size - 4, -size);

    graphics.lineBetween(-size, size - 4, -size, size);
    graphics.lineBetween(-size, size, -size + 4, size);

    graphics.lineBetween(size, size - 4, size, size);
    graphics.lineBetween(size, size, size - 4, size);
  }

  /**
   * Update scanner button appearance
   */
  private updateScannerButton(active: boolean): void {
    const bg = this.scannerButton.list[0] as Phaser.GameObjects.Graphics;
    const icon = this.scannerButton.list[1] as Phaser.GameObjects.Graphics;

    bg.clear();
    if (active) {
      bg.fillStyle(MicroscopeHUD.COLORS.scannerActive, 0.3);
      bg.fillCircle(0, 0, 20);
      bg.lineStyle(2, MicroscopeHUD.COLORS.scannerActive, 1);
      bg.strokeCircle(0, 0, 20);
    } else {
      bg.fillStyle(MicroscopeHUD.COLORS.buttonBg, 0.9);
      bg.fillCircle(0, 0, 20);
      bg.lineStyle(2, MicroscopeHUD.COLORS.accent, 0.7);
      bg.strokeCircle(0, 0, 20);
    }

    this.drawScannerIcon(icon, active);
  }

  /**
   * Create scale indicator
   */
  private createScaleIndicator(): void {
    const y = this.config.height / 2 - 15;

    this.scaleIndicator = this.scene.add.text(0, y, this.getScaleText(), {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '10px',
      color: '#68d391',
    });
    this.scaleIndicator.setOrigin(0.5);

    this.container.add(this.scaleIndicator);
  }

  /**
   * Get scale text based on current zoom
   */
  private getScaleText(): string {
    const zoom = this.microscope.getZoomLevel();
    if (zoom >= 1000) return '~0.1μm';
    if (zoom >= 400) return '~1μm';
    if (zoom >= 100) return '~10μm';
    if (zoom >= 40) return '~25μm';
    if (zoom >= 10) return '~100μm';
    return '~1mm';
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    this.scene.events.on('microscope:zoom', this.handleZoom, this);
    this.scene.events.on('microscope:scanner', this.handleScannerToggle, this);
  }

  /**
   * Handle zoom event
   */
  private handleZoom(_event: ZoomEvent): void {
    this.updateActiveZoom();
    this.scaleIndicator.setText(this.getScaleText());
  }

  /**
   * Handle scanner toggle
   */
  private handleScannerToggle(active: boolean): void {
    this.updateScannerButton(active);
  }

  /**
   * Update which zoom button is active
   */
  private updateActiveZoom(): void {
    const currentLevel = this.microscope.getZoomLevel();

    this.zoomButtons.forEach((button, index) => {
      const level = MicroscopeSystem.ZOOM_LEVELS[index];
      const isActive = level === currentLevel;

      button.isActive = isActive;
      this.drawButtonBg(button.bg, 50, 30, false, isActive);

      if (isActive) {
        button.label.setColor('#ffffff');
      } else {
        button.label.setColor('#e2e8f0');
      }
    });
  }

  /**
   * Set visibility
   */
  public setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Set position
   */
  public setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Get the container
   */
  public getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off('microscope:zoom', this.handleZoom, this);
    this.scene.events.off('microscope:scanner', this.handleScannerToggle, this);
    this.container.destroy();
  }
}
